/**
 * @file components/dispatch/CaseDocumentReviewPanel.tsx
 * @description 案件應附文件提交面板（序57 新版：提交即生效，無獨立審核關卡）。
 *
 * 用於：
 *   1. dispatch/new wizard 步驟 5 上方（gate 未通過時阻擋報價表單）
 *   2. dispatch/:caseId 詳情頁的「應附文件」分頁頂部
 *
 * 序57 新版語義：
 *   - 「文件審核／文件點檢」更名【應附文件】，**刪除審核（通過/不通過決議）功能**；
 *   - 作業人員上傳應附文件後直接「提交」：後端寫 status='approved' 並自動生成合約評審；
 *   - 應附文件清單有分類未上傳時，【未上傳檔案原因說明】必填；
 *   - 系統自動記錄作業人員（submittedBy → 姓名展示）；
 *   - 開放稽核員作業（capability case-document-review.submit = admin/manager/auditor）。
 *
 * 存量資料兼容：舊流程可能留有 pending 紀錄，manager 可用簡化後備按鈕決議清掉。
 *
 * 註：本元件目前沒有 RTL render 測試 —— 元件 UI 行為由手動回歸覆蓋；
 * 純函式 isReviewApproved 由 CaseDocumentReviewPanel.test.ts 覆蓋。
 */

import { useState } from 'react';
import { Alert, Button, Space, Tag, Timeline, Typography } from 'antd';
import { message, modal } from '@/lib/antd-app';
import { Send } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { apiClient } from '@/lib/api-client';
import { userCan } from '@zhcb/shared';
import type { CaseDocumentReviewDto } from '@zhcb/shared';

/** 不通過原因分類（舊資料展示用）對應的中文標籤 */
const REJECTION_REASON_LABEL: Record<'incomplete' | 'unclear', string> = {
  incomplete: '文件不齊全，需補充',
  unclear: '文件不清晰，需重傳',
};

interface Props {
  /** 案件 ID */
  caseId: number;
  /** 案件編號（提示文案用） */
  caseCode?: string;
  /**
   * 目前 actor user id + 能力/角色資訊（從 auth store 傳入）。
   * 含 capabilities 時 userCan 以之為準；無則回落 roles → DEFAULT_ROLE_CAPABILITIES。
   */
  actor: { id: number; roles: string[]; capabilities?: string[] };
  /** 目前案件 case_documents 的 id 集合（用於 snapshot diff 提示） */
  currentDocumentIds: number[];
  /**
   * 尚未上傳檔案的應附文件分類標籤（序57：有缺漏時「未上傳原因說明」必填）。
   * 由父層依 CASE_DOC_CATEGORIES 與已上傳 docType 差集計算傳入。
   */
  missingCategories?: string[];
  /**
   * 序129 內聯化：未上傳原因改由各分類行內聯輸入，狀態上提至父層（DocumentsTab）統一持有，
   * 以 { 分類標籤: 原因 } 保存。本面板僅負責讀取校驗＋提交序列化，不再自渲染原因輸入框。
   */
  perReason?: Record<string, string>;
  /** 未上傳原因變更回呼（提交成功後由本面板呼叫並傳 {} 清空） */
  onPerReasonChange?: (next: Record<string, string>) => void;
  /** 是否開啟 10s 輪詢（wizard 開、詳情頁不開） */
  poll?: boolean;
  /** 審核通過時的回調（wizard 用來解鎖報價表單） */
  onApproved?: () => void;
}

const STATUS_TAG: Record<'pending' | 'approved' | 'rejected', { color: string; label: string }> = {
  pending: { color: 'blue', label: '點檢中（舊流程）' },
  approved: { color: 'green', label: '已提交' },
  rejected: { color: 'red', label: '未通過（舊流程）' },
};

export function CaseDocumentReviewPanel({
  caseId,
  caseCode,
  actor,
  currentDocumentIds,
  missingCategories = [],
  perReason = {},
  onPerReasonChange,
  poll = false,
  onApproved,
}: Props) {
  const qc = useQueryClient();

  // 未上傳檔案原因說明（序129）：狀態由父層 DocumentsTab 持有（各分類行內聯輸入），
  // 以 { 分類: 原因 } 傳入；提交時序列化為多行「分類：原因」寫入既有 missingReason 文字欄（無需改 schema）。

  // 最新一條審核
  const latestQuery = useQuery<{ item: CaseDocumentReviewDto | null }>({
    queryKey: ['case-doc-review-latest', caseId],
    queryFn: () => apiClient.get(`/api/cases/${caseId}/document-reviews/latest`),
    refetchInterval: poll ? 10_000 : false,
  });

  // 流水（折疊在 Timeline 裡）
  const listQuery = useQuery<{ items: CaseDocumentReviewDto[] }>({
    queryKey: ['case-doc-review-list', caseId],
    queryFn: () => apiClient.get(`/api/cases/${caseId}/document-reviews`),
  });

  const latest = latestQuery.data?.item ?? null;
  const isManager = actor.roles.includes('manager') || actor.roles.includes('admin');
  // 序57.6：開放稽核員作業——以能力矩陣為準（admin/manager/auditor）
  // actor 為 { id, roles }（從 auth store 傳入），userCan 無 capabilities 時自動回落 roles 矩陣
  const canOperate = userCan(actor, 'case-document-review.submit');

  // 通過回調：每次 latest 變更為 approved 時觸發一次
  const [lastNotifiedApprovedId, setLastNotifiedApprovedId] = useState<number | null>(null);
  if (latest?.status === 'approved' && latest.id !== lastNotifiedApprovedId) {
    setLastNotifiedApprovedId(latest.id);
    onApproved?.();
  }

  // ─── submit mutation（序57 新版：提交即生效） ────────────────────────────────
  const submitMutation = useMutation({
    mutationFn: async (v: { missingReason?: string }) => {
      return apiClient.post<{ item: CaseDocumentReviewDto }>(
        `/api/cases/${caseId}/document-reviews`,
        { missingReason: v.missingReason },
      );
    },
    onSuccess: () => {
      message.success('應附文件已提交，合約評審已生成');
      onPerReasonChange?.({});
      void qc.invalidateQueries({ queryKey: ['case-doc-review-latest', caseId] });
      void qc.invalidateQueries({ queryKey: ['case-doc-review-list', caseId] });
    },
    onError: (err: Error) => {
      // 業務必要：提交失敗需輸出錯誤日誌
      console.error('[CaseDocumentReviewPanel] submit failed', err);
      message.error(err.message ?? '提交失敗');
    },
  });

  // ─── 存量 pending 後備決議（僅 manager，可清掉舊流程遺留的待決議紀錄） ────────
  const decideMutation = useMutation({
    mutationFn: async (v: {
      reviewId: number;
      decision: 'approved' | 'rejected';
      comment?: string;
    }) => {
      return apiClient.post<{ item: CaseDocumentReviewDto }>(
        `/api/cases/${caseId}/document-reviews/${v.reviewId}/decide`,
        { decision: v.decision, comment: v.comment },
      );
    },
    onSuccess: () => {
      message.success('已處理舊流程點檢紀錄');
      void qc.invalidateQueries({ queryKey: ['case-doc-review-latest', caseId] });
      void qc.invalidateQueries({ queryKey: ['case-doc-review-list', caseId] });
    },
    onError: (err: Error) => {
      // 業務必要：決議失敗需輸出錯誤日誌
      console.error('[CaseDocumentReviewPanel] decide failed', err);
      message.error(err.message ?? '處理失敗');
    },
  });

  // ─── 文件變更 diff（snapshot vs currentDocumentIds） ──────────────────────────
  const snapshot = latest?.snapshotDocumentIds ?? [];
  const snapshotSet = new Set(snapshot);
  const currentSet = new Set(currentDocumentIds);
  const docsChanged =
    snapshot.length !== currentDocumentIds.length ||
    snapshot.some((id) => !currentSet.has(id)) ||
    currentDocumentIds.some((id) => !snapshotSet.has(id));

  // ─── 前置條件校驗：文件為空時不允許提交 ──────────────────────────────────────
  const missingDocuments = currentDocumentIds.length === 0;
  // 序57/129：有分類缺漏時，每個缺漏分類的原因皆必填
  const needMissingReason = missingCategories.length > 0;
  const allReasonsFilled = missingCategories.every((c) => (perReason[c] ?? '').trim().length > 0);
  const canSubmit = !missingDocuments && (!needMissingReason || allReasonsFilled);

  const handleSubmit = () => {
    if (missingDocuments) return;
    if (needMissingReason && !allReasonsFilled) {
      message.error('尚有應附文件未上傳，請逐項填寫每個未上傳文件的原因');
      return;
    }
    // 序129：序列化為多行「分類：原因」寫入 missingReason
    const serialized = missingCategories
      .map((c) => `${c}：${(perReason[c] ?? '').trim()}`)
      .join('\n');
    submitMutation.mutate({
      missingReason: needMissingReason ? serialized : undefined,
    });
  };

  // ─── 子元件：提交（序57：缺漏原因必填 + 提交生成合約評審） ─────────────────────
  const SubmitForm = (
    <div style={{ marginTop: 12 }}>
      {needMissingReason && (
        <div style={{ marginBottom: 8 }}>
          {/* 序129 內聯化：原因輸入框已移至下方各分類行內；此處僅保留缺漏提示與未填數量。 */}
          <Alert
            type="warning"
            showIcon
            message={`尚有 ${missingCategories.length} 類應附文件未上傳，請於下方各分類逐項填寫未上傳原因（必填 *）`}
            description={
              !allReasonsFilled ? (
                <Typography.Text type="danger" style={{ fontSize: 13 }}>
                  尚有 {missingCategories.filter((c) => !(perReason[c] ?? '').trim()).length}{' '}
                  項未填寫原因。
                </Typography.Text>
              ) : undefined
            }
            style={{ marginBottom: 8 }}
          />
        </div>
      )}
      <Button
        type="primary"
        icon={<Send size={14} />}
        loading={submitMutation.isPending}
        disabled={!canSubmit}
        title={missingDocuments ? '請先上傳應附文件' : undefined}
        onClick={handleSubmit}
      >
        提交應附文件（生成合約評審）
      </Button>
    </div>
  );

  // ─── 主體 ─────────────────────────────────────────────────────────────────────
  if (latestQuery.isLoading) {
    return <Alert type="info" message="正在讀取應附文件提交狀態…" />;
  }

  // status = none → 顯示「提交」（具作業能力者可見；缺文件時不顯示按鈕）
  if (!latest) {
    return <div>{canOperate && !missingDocuments && SubmitForm}</div>;
  }

  const tag = STATUS_TAG[latest.status];

  return (
    <div>
      <Alert
        type={
          latest.status === 'approved' ? 'success' : latest.status === 'rejected' ? 'error' : 'info'
        }
        showIcon
        message={
          <Space>
            <span>應附文件狀態：</span>
            <Tag color={tag.color}>{tag.label}</Tag>
            {caseCode && <Typography.Text type="secondary">{caseCode}</Typography.Text>}
          </Space>
        }
        description={
          <div>
            <div>
              作業人員：{latest.submitterName ?? `#${latest.submittedBy}`}・提交時間：
              {dayjs(latest.submittedAt).format('YYYY-MM-DD HH:mm')}
            </div>
            {latest.submitterNote && <div>備注：{latest.submitterNote}</div>}
            {/* 序57：未上傳檔案原因說明 */}
            {latest.missingReason && <div>未上傳檔案原因說明：{latest.missingReason}</div>}
            {/* 舊流程遺留欄位展示（決議人/意見/不通過原因） */}
            {latest.decidedAt && latest.decidedBy !== latest.submittedBy && (
              <div>
                處理人：{latest.deciderName ?? `#${latest.decidedBy}`}
                {dayjs(latest.decidedAt).format('YYYY-MM-DD HH:mm')}
                {latest.decisionComment && <> 意見：{latest.decisionComment}</>}
              </div>
            )}
            {latest.status === 'rejected' && latest.rejectionReasonType && (
              <div>原因：{REJECTION_REASON_LABEL[latest.rejectionReasonType]}</div>
            )}
          </div>
        }
        style={{ marginBottom: 12 }}
      />

      {/* 已提交後若文件已變更 → 提示重新提交 */}
      {latest.status === 'approved' && docsChanged && canOperate && (
        <div style={{ marginBottom: 12 }}>
          <Alert
            type="warning"
            showIcon
            message="文件已變更，建議重新提交"
            description="自上次提交後，應附文件清單發生了變化。如有實質性修改，請重新提交。"
          />
          {SubmitForm}
        </div>
      )}

      {/* 舊流程 rejected 後可重新提交 */}
      {latest.status === 'rejected' && canOperate && (
        <div style={{ marginBottom: 12 }}>{SubmitForm}</div>
      )}

      {/* 存量 pending 後備處理（僅 manager；序57 已刪除審核功能，此為舊資料清理入口） */}
      {latest.status === 'pending' && isManager && (
        <Space style={{ marginTop: 12 }}>
          <Button
            type="primary"
            loading={decideMutation.isPending}
            onClick={() =>
              modal.confirm({
                title: '確認此筆舊流程點檢紀錄？',
                content: '序57 新流程已無審核關卡；確認後此紀錄將標記為已提交（生效）。',
                okText: '確認',
                cancelText: '取消',
                onOk: () =>
                  decideMutation.mutateAsync({ reviewId: latest.id, decision: 'approved' }),
              })
            }
          >
            確認生效（舊紀錄）
          </Button>
        </Space>
      )}

      {/* 流水（折疊） */}
      {(listQuery.data?.items ?? []).length > 1 && (
        <details style={{ marginTop: 12 }}>
          <summary style={{ cursor: 'pointer' }}>查看歷史提交記錄</summary>
          <Timeline
            style={{ marginTop: 12 }}
            items={(listQuery.data?.items ?? []).map((r) => ({
              color: STATUS_TAG[r.status].color,
              children: (
                <div>
                  <Tag color={STATUS_TAG[r.status].color}>{STATUS_TAG[r.status].label}</Tag>
                  <span style={{ marginLeft: 8 }}>
                    {dayjs(r.submittedAt).format('YYYY-MM-DD HH:mm')} 由{' '}
                    {r.submitterName ?? `#${r.submittedBy}`} 提交
                  </span>
                  {r.missingReason && (
                    <div style={{ color: '#666', marginLeft: 8, whiteSpace: 'pre-line' }}>
                      未上傳原因：{'\n'}
                      {r.missingReason}
                    </div>
                  )}
                  {r.decisionComment && (
                    <div style={{ color: '#666', marginLeft: 8 }}>意見：{r.decisionComment}</div>
                  )}
                </div>
              ),
            }))}
          />
        </details>
      )}
    </div>
  );
}

/**
 * 工具函數：判斷給定 review 是否解鎖了下游報價/繳費（純函數，便於測試）。
 */
export function isReviewApproved(review: CaseDocumentReviewDto | null | undefined): boolean {
  return review?.status === 'approved';
}
